<script setup lang="ts">
import { toast } from "vue3-toastify";
import { removeEmptyStringKeys } from "~/composables/register-data";
import { useIframeAuth } from "~/composables/iframe";

definePageMeta({
  middleware: "guest",
  layout: "empty",
});

const { isIframe } = useIframeAuth();

const enviroment = useRuntimeConfig().public.enviroment;
const { t, locale } = useI18n();
const localePath = useLocalePath();
const { track } = useMetaPixel();

const message = ref("");
const successMessage = ref("");

const submittedFormSuccessfully = ref(false);

async function submit(data: any) {
  const registerData = removeEmptyStringKeys(data);
  let registerUrl = useRequestURL().origin;

  if (enviroment !== "production" && locale.value !== "es") {
    registerUrl = registerUrl + "/" + locale.value;
  }

  const redirectUrl = encodeURIComponent(t("iFrame.redirectUrl"));
  const verification_url = isIframe.value
    ? registerUrl + "/verify?redirect_url=" + redirectUrl
    : registerUrl + "/verify";

  const metaEventId = createMetaEventId();

  const response = await useFetch("/api/auth", {
    method: "POST",
    body: {
      data: registerData,
      verification_url: verification_url,
      locale: locale.value,
      meta: { event_id: metaEventId, content_category: "family-card" },
    },
  });

  // Handle response
  if (response.data?.value?.success) {
    track("Lead", { content_category: "family-card" }, { eventId: metaEventId });

    if (isIframe.value) {
      submittedFormSuccessfully.value = true;
      // scrollToTopOfForm();
      successMessage.value = t("iFrame.checkEmail");
      // toast.success(t("iFrame.checkEmail"), {
      //   autoClose: 16000
      // });
    } else {
      submittedFormSuccessfully.value = false;

      await navigateTo(localePath("/verify-email"));
    }
  } else {
    // Handle errors and show error message
    scrollToTopOfForm();
    message.value = response.data?.value?.message || "An error occurred.";
    toast.error(message.value);
  }
}

function scrollToTopOfForm() {
  const element = document.getElementById("top-of-form");
  if (element) {
    element.scrollIntoView({ behavior: "smooth" });
  }
}

onMounted(() => {
  track("ViewContent", { content_name: "register", content_category: "family-card" });

  if (!isIframe.value) {
    setPageLayout("default");
  }
  nextTick(() => {
    useHead({
      script: [
        {
          children: `
                function sendHeightToParent() {
                  const height = document.body.scrollHeight + 100;
                  window.parent.postMessage({ height }, '*');
                }

                // Listen for postMessage from the parent
                window.addEventListener("message", function (event) {
                  if (event.data === "getHeight") {
                    sendHeightToParent();
                  }
                });

                // Observe changes in the DOM to detect height changes
                const observer = new MutationObserver(() => {
                  sendHeightToParent();
                });

                observer.observe(document.body, {
                  childList: true,
                  subtree: true,
                  attributes: true,
                  characterData: true
                });
              `,
        },
      ],
    });
  });
});
</script>

<template>
  <div v-if="!isIframe">
    <div class="bg-gradient-to-r from-20% from-[#E72B6F] to-[#1450A0] to-80%">
      <div
        class="container mx-auto flex justify-center md:justify-end items-center py-7 md:py-11 relative overflow-hidden"
      >
        <nuxt-img
          src="images/family-card.png"
          alt="family-card"
          class="object-contain max-w-md w-full md:max-w-[180px] h-auto hidden md:block absolute top-1/2 transform -translate-y-1/2 left-0"
        />
        <h1 class="text-lg sm:text-2xl text-white relative text-end">
          {{ t("userForm.headerTitle") }}
        </h1>
      </div>
    </div>
    <div class="container mx-auto pb-8">
      <div class="max-w-screen-lg mx-auto md:px-10 my-12">
        <div
          class="text-brand-secondary font-semibold text-lg md:text-3xl uppercase"
        >
          {{ t("userForm.title") }}
        </div>
        <div class="text-base md:text-lg text-brand-gray mt-4 md:mt-1">
          {{ t("userForm.description") }}
        </div>
      </div>
      <UserForm @submit="submit" />
    </div>
  </div>
  <div v-else>
    <UserForm
      @submit="submit"
      :registerButtonIframeTitle="t('iFrame.registerTitle')"
      :successMessage="successMessage"
      :disableAfterSubmit="submittedFormSuccessfully"
    />
  </div>
</template>
